import React from 'react';
import { useTranslation } from 'react-i18next';
import { Box, MenuItem, Typography } from '@mui/material';
import EditNoteOutlinedIcon from '@mui/icons-material/EditNoteOutlined';
import CallSplitIcon from '@mui/icons-material/CallSplit';

export type NewJournalKind = 'STANDARD' | 'INTERACTIVE';

export interface NewJournalMenuItemsProps {
  /**
   * Called with the chosen kind. Caller is responsible for navigation —
   * different sites navigate from different scopes (router, parent
   * callback, drawer-close-then-route, etc.) so we don't bake the
   * navigation into this presentational component.
   */
  onPick: (kind: NewJournalKind) => void;
}

/**
 * The two MenuItem rows used by every "new journal" entry point: the
 * standard long-form journal option and the interactive (CYOA) story
 * option, each with an icon, primary label, and a one-line description.
 *
 * Why a separate, presentational component:
 *   • Both `NewJournalSplitButton` (Popper-based) and `AppLayout`'s top-
 *     nav menu (Menu-based) want exactly these two rows. Centralising
 *     them keeps copy + iconography aligned across the app — when the
 *     CYOA description changes once, every entry point picks it up.
 *   • Mobile drawer renders these without a popper at all, as plain
 *     stacked links. That callsite uses its own visual primitives, but
 *     the COPY comes from the same translation keys, so a string change
 *     here propagates everywhere automatically.
 */
const NewJournalMenuItems: React.FC<NewJournalMenuItemsProps> = ({ onPick }) => {
  const { t } = useTranslation();

  return (
    <>
      <MenuItem onClick={() => onPick('STANDARD')}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
          <EditNoteOutlinedIcon fontSize="small" sx={{ mt: 0.25 }} />
          <Box>
            <Typography variant="body2" fontWeight={600}>
              {t('pages.journals.myJournals.standardJournal')}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {t('pages.journals.myJournals.standardJournalDescription')}
            </Typography>
          </Box>
        </Box>
      </MenuItem>
      <MenuItem onClick={() => onPick('INTERACTIVE')}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
          <CallSplitIcon fontSize="small" sx={{ mt: 0.25 }} />
          <Box>
            <Typography variant="body2" fontWeight={600}>
              {t('pages.journals.myJournals.newInteractive')}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {t('pages.journals.myJournals.newInteractiveDescription')}
            </Typography>
          </Box>
        </Box>
      </MenuItem>
    </>
  );
};

export default NewJournalMenuItems;
